import type { UserRole } from '@yrp/shared/user';
import type { z } from 'zod';
import type { WebsiteActivityQuery } from '@yrp/shared/website-activity';

export const ACTIVITY_WINDOWS = [7, 30, 90] as const;
export type ActivityWindow = z.infer<typeof WebsiteActivityQuery>['days'];
export const ACTIVITY_VIEWS = ['overview', 'registrations', 'people', 'visitors', 'listings', 'acquisition', 'coverage'] as const;
export type ActivityView = (typeof ACTIVITY_VIEWS)[number];

export function activityWindow(value: unknown): ActivityWindow {
  return value === 7 || value === '7' ? 7 : value === 90 || value === '90' ? 90 : 30;
}

export function activityView(value: unknown): ActivityView {
  return ACTIVITY_VIEWS.find((view) => view === value) ?? 'registrations';
}

export function activityViews(role: UserRole | undefined): readonly ActivityView[] {
  return ACTIVITY_VIEWS.filter((view) => view !== 'visitors' || role === 'admin');
}

export function activityViewForRole(value: unknown, role: UserRole | undefined): ActivityView {
  const view = activityView(value);
  return view === 'visitors' && role !== 'admin' ? 'overview' : view;
}

export function activitySearch(search: Record<string, unknown>): { days?: ActivityWindow; view?: ActivityView } {
  return { days: activityWindow(search.days), view: activityView(search.view) };
}

export function contactActivitySearch(search: Record<string, unknown>): { tab?: 'website'; compose?: 'popout' } {
  return { ...(search.tab === 'website' ? { tab: 'website' as const } : {}), ...(search.compose === 'popout' ? { compose: 'popout' as const } : {}) };
}

/** An admin's explicit choice is required on every scope transition. */
export function activityAll(role: UserRole | undefined, requested: boolean): boolean {
  return role === 'admin' && requested;
}

export function activityQuery(days: ActivityWindow, all: boolean, cursor?: string): string {
  const query = new URLSearchParams({ days: String(days) });
  if (all) query.set('all', 'true');
  if (cursor && /^\d{1,16}$/.test(cursor)) query.set('cursor', cursor);
  return query.toString();
}

export function activityDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'Unavailable';
  if (ms === 0) return '0s';
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function activityDate(value: string | null): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unavailable';
  return date.toLocaleString('en-US', {
    timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function activityIdentity(value: string): string {
  if (value === 'authenticated') return 'Signed-in account';
  if (value === 'same_browser_before_sign_in') return 'Same browser before sign-in';
  if (value === 'submitted_email') return 'Email submitted — unverified';
  if (value === 'anonymous') return 'Anonymous browser';
  return 'Identity not established';
}

const EVENT_LABELS: Readonly<Record<string, string>> = {
  visit: 'Started a visit', visit_started: 'Started a visit', sign_in: 'Signed in', sign_out: 'Signed out',
  page_view: 'Opened a page', listing_view: 'Viewed a listing', active_time: 'Active browsing',
  search: 'Searched for homes', favorite_added: 'Saved a home', favorite_removed: 'Removed a saved home',
  saved_search_created: 'Saved a search', saved_search_updated: 'Updated a search', saved_search_deleted: 'Deleted a search',
  share_opened: 'Opened sharing options', share_action: 'Used a sharing option', share: 'Used a sharing option',
  inquiry: 'Sent an inquiry', tour_requested: 'Requested a showing', valuation_requested: 'Requested a valuation',
  lead_submitted: 'Website form submitted', registration_completed: 'Completed registration', interaction: 'Used a website feature',
  gallery_opened: 'Opened listing photos', tour_opened: 'Opened a virtual tour', video_started: 'Started a listing video',
  map_interacted: 'Used the map', cta_clicked: 'Used a listing action',
};

export function activityEventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replaceAll('_', ' ');
}

/** Canonical listing identity only. No arbitrary URLs or capability tokens. */
export function activityListingParams(market: string | null, key: string | null): { market: string; key: string } | null {
  if ((market !== 'yuma' && market !== 'tucson') || !key || !/^[a-zA-Z0-9_-]+$/.test(key)) return null;
  return { market, key };
}

export function activityAccessDenied(error: unknown): boolean {
  const status = error && typeof error === 'object' && 'statusCode' in error ? error.statusCode : undefined;
  return status === 403 || status === 404;
}

export function activityError(error: unknown): string {
  if (activityAccessDenied(error)) return 'Website activity is unavailable for this scope. Contact access does not automatically grant access to website activity.';
  return 'Website activity could not be loaded. These are unavailable results, not zero activity. Try again.';
}
